import { CheckIcon, type IconT, LoaderIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { animated, useTransition, type SpringValues } from '@react-spring/web';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type {
  SlideSpecificActionI18nNamespace,
  SlideSpecificActionI18nKey,
} from '@mentimeter/i18n-presenting';

export interface ActionButtonProps {
  onClick: () => void;
  id: string;
  hotkey: string | undefined;
  isLoading: boolean;
  success?: boolean;
  successText?: string;
  slideId?: string;
  icon?: undefined | ((props: IconT) => React.JSX.Element);
  enableHotkeys: boolean;
  slideSpecificActionsTranslationKey: SlideSpecificActionI18nKey;
}

export const ActionButton = ({
  onClick,
  id,
  success,
  successText,
  slideId,
  hotkey: hotkeyFromProps,
  isLoading,
  icon,
  enableHotkeys,
  slideSpecificActionsTranslationKey,
}: ActionButtonProps) => {
  const t = useTranslations<SlideSpecificActionI18nNamespace>(
    // @ts-expect-error(2345) we need to narrow type to prevent TS from crashing. We rely on i18n tests to ensure all actions have the same structure.
    `presenting.slide_specific_actions.${slideSpecificActionsTranslationKey}`,
  );

  const hotkey = enableHotkeys && hotkeyFromProps ? hotkeyFromProps : 'click';

  //The slideId is needed for the button to re-animate when changing slide
  const transition = useTransition(`${id}-${slideId}`, {
    from: {
      opacity: 0,
      transform: 'translate3d(0, 60%, 0)',
    },
    enter: {
      opacity: 1,
      delay: 300,
      transform: 'translate3d(0, 0%, 0)',
    },
  });

  return (
    <Clickable
      onClick={onClick}
      borderRadius="full"
      aria-label={t(
        'slide_controls_button_a11y',
        //@ts-expect-error(2345) i18n library can't handle TS for values yet
        { hotkey },
      )}
      extend={() => ({
        '@media (hover: hover)': {
          ':hover': {
            opacity: 1,
          },
        },
      })}
    >
      {transition((styles, show) => {
        if (!show) return null;
        if (success && successText) {
          return (
            <Box as={animated.div} style={styles as SpringValues}>
              <Box
                bg="primary"
                borderRadius={3}
                pl="space6"
                pr="space6"
                height="56px"
                alignItems="center"
                flexDirection="row"
              >
                <Box mr={2}>
                  <CheckIcon color="onPrimary" />
                </Box>
                <Text color="onPrimary" fontSize="20px">
                  {successText}
                </Text>
              </Box>
            </Box>
          );
        }
        return (
          <Box
            as={animated.div}
            style={styles as SpringValues}
            alignItems="center"
            width="100%"
            borderRadius="full"
            bg="surfaceOverlayTransparent"
            px="space6"
            extend={({ theme }) => ({
              backdropFilter: theme.kosmosBlur.xl,
              WebkitBackdropFilter: theme.kosmosBlur.xl,
              '&:hover': {
                backgroundColor: theme.colors.surfaceOverlayTransparentHover,
              },
            })}
          >
            <Box height="56px" justifyContent="center">
              {isLoading ? (
                <LoaderIcon color="primary" size={3} />
              ) : (
                <Text
                  fontWeight="regular"
                  fontSize="100"
                  lineHeight="relaxed"
                  color="primary"
                  alignItems="center"
                  display="inline-flex"
                >
                  {icon && (
                    <Text display="inline-flex" mr="space2">
                      {icon({ size: 1, color: 'text' })}
                    </Text>
                  )}
                  {t.rich('slide_controls_button', {
                    hotkey,
                    Hotkey,
                  })}
                </Text>
              )}
            </Box>
          </Box>
        );
      })}
    </Clickable>
  );
};

function Hotkey(chunks: ReactNode) {
  return (
    <Text
      color="onPrimary"
      px="space2"
      mx="space1"
      pt="space1"
      pb="3px"
      fontSize="87.5"
      fontWeight="semiBold"
      bg="primary"
      textTransform="uppercase"
      extend={({ theme }) => ({
        borderRadius: `${theme.kosmosBorderRadius.lg}px`,
      })}
    >
      {chunks}
    </Text>
  );
}
